import {ReactElement} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledHeadingVariant} from "@/shared/StyledComponents";
import {Button, Typography, useMediaQuery, useTheme} from "@mui/material";
import {PD_LG, PD_SM, PD_XLG, PD_XLG2, PD_XLG3} from "@/shared/dimens";
import {HighlightSectionData, SiteTemplate} from "../../types";

const HIGHLIGHT_BG = "#0D2918";

export function HighlightSection(props: { data: HighlightSectionData, template: SiteTemplate }): ReactElement {
  const theme = useTheme();
  const sm = useMediaQuery(theme.breakpoints.up("sm"));
  const md = useMediaQuery(theme.breakpoints.up("md"));

  const imagePane = <div style={{flex: "1 0 42%", minHeight: sm ? 480 : 280, position: "relative"}}>
    <img
      src={props.data.imageUrl}
      style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: "center",
        display: "block",
      }}/>
    <div style={{
      position: "absolute",
      inset: 0,
      background: props.data.imagePosition === "left"
        ? "linear-gradient(to right, transparent 60%, rgba(13,41,24,0.5) 100%)"
        : "linear-gradient(to left, transparent 60%, rgba(13,41,24,0.5) 100%)",
    }}/>
  </div>;

  const textPane = <div style={{
    flex: "1 0 42%",
    display: "flex",
    alignItems: "center",
    padding: md ? `${PD_XLG3}px ${PD_XLG2}px` : sm ? `${PD_XLG2}px` : `${PD_XLG}px ${PD_LG}px`,
    borderLeft: props.data.imagePosition !== "left" ? `4px solid ${theme.palette.secondary.main}` : "none",
    borderRight: props.data.imagePosition === "left" ? `4px solid ${theme.palette.secondary.main}` : "none",
  }}>
    <StyledBoxColumn style={{gap: PD_XLG, maxWidth: 520}}>
      <StyledBoxColumn style={{gap: PD_LG}}>
        {props.data.eyebrow
          ? <Typography
            variant="overline"
            style={{letterSpacing: 3, fontWeight: "700", color: theme.palette.secondary.main}}>
            {props.data.eyebrow}
          </Typography>
          : null}
        {props.data.title
          ? <StyledHeadingVariant default_={2} style={{color: "white"}}>
            {props.data.title}
          </StyledHeadingVariant>
          : null}
        {props.data.text
          ? <Typography style={{color: "rgba(255,255,255,0.65)", lineHeight: 1.8}}>
            {props.data.text}
          </Typography>
          : null}
      </StyledBoxColumn>
      {props.data.links?.length > 0
        ? <StyledBoxRow style={{flexWrap: "wrap", gap: PD_SM}}>
          {props.data.links.map((link, i) =>
            <Button
              key={i}
              href={link.url}
              color={i === 0 ? "secondary" : undefined}
              variant={i === 0 ? "contained" : "outlined"}
              style={i !== 0 ? {color: "white", borderColor: "rgba(255,255,255,0.4)"} : undefined}>
              {link.text}
            </Button>
          )}
        </StyledBoxRow>
        : null}
    </StyledBoxColumn>
  </div>;

  const imageLeft = props.data.imagePosition === "left";

  return <div style={{display: "flex", flexWrap: "wrap", width: "100%", backgroundColor: HIGHLIGHT_BG}}>
    {imageLeft
      ? <>{imagePane}{textPane}</>
      : <>{textPane}{imagePane}</>}
  </div>;
}
